import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SignUpDialog from "@/components/SignUpDialog";
import { useAuth } from "@/hooks/useAuth";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import heroImg from "@/assets/hero-community-hands.jpg";

const Hero = () => {
  const [signUpOpen, setSignUpOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { needsWelcome, hasIdea, hasCompanyProfile, progress, loading } =
    useOnboardingStatus();

  const ctaLabel = !user
    ? "Get Started"
    : needsWelcome
      ? "Continue Setup"
      : !hasIdea
        ? "Post an Idea"
        : !hasCompanyProfile
          ? "Open Your Coach"
          : progress >= 1
            ? "Go to Profile"
            : "Continue Setup";

  const handleCta = () => {
    if (!user) return setSignUpOpen(true);
    if (loading) return;
    if (needsWelcome) return navigate("/welcome");
    if (!hasIdea) return navigate("/ideas?new=1");
    if (!hasCompanyProfile) return navigate("/coach");
    return navigate("/profile");
  };

  return (
    <>
    <SignUpDialog open={signUpOpen} onOpenChange={setSignUpOpen} />
    <section className="relative bg-hero overflow-hidden pt-20">
      {/* Hero photo backdrop on right half */}
      <div className="absolute inset-y-0 right-0 w-full md:w-3/5 lg:w-1/2">
        <img
          src={heroImg}
          alt="Two pairs of hands clasped together over an Alabama map"
          className="w-full h-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(140_12%_14%)] via-[hsl(140_12%_14%)]/60 to-transparent md:via-transparent md:from-[hsl(140_12%_14%)]/90" />
      </div>

      <div className="container mx-auto px-6 pt-12 md:pt-20 pb-0 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-start min-h-[70vh]">
          <div className="max-w-xl">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-3 mb-6 text-hero-muted text-xs font-semibold tracking-[0.2em] uppercase"
            >
              <span>Alabama</span>
              <span className="w-1 h-1 rounded-full bg-current opacity-60" />
              <span>Civic Infrastructure</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-hero-fg mb-6"
            >
              Powering the work that moves communities forward.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="text-hero-muted text-base md:text-lg max-w-md mb-10 leading-relaxed"
            >
              A government affairs firm built for small, community-rooted Alabama organizations.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mb-20"
            >
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 font-display font-semibold text-base px-7 h-12 rounded-full shadow-glow"
                onClick={handleCta}
              >
                {ctaLabel} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </motion.div>
          </div>

          {/* Spacer for photo column */}
          <div className="hidden md:block" aria-hidden />
        </div>

        {/* Massive overlapping wordmark */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="relative -mt-4 md:-mt-8 pointer-events-none select-none"
        >
          <h2 className="font-display font-bold leading-[0.85] tracking-tight text-hero-fg text-[18vw] md:text-[16vw] whitespace-nowrap">
            Emerging<br className="md:hidden" />
            <span className="md:ml-[0.2em]">Market</span>
          </h2>
        </motion.div>
      </div>
    </section>
    </>);

};

export default Hero;